'use client';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

// ─── Types ────────────────────────────────────────────────────────────────────
type ActivityType = 'challenge_log' | 'goal_completed' | 'milestone_celebrated' | 'streak_achievement';
type FilterType = 'all' | ActivityType;

interface ActivityItem {
  id: string;
  user_id: string;
  activity_type: ActivityType;
  title: string;
  description: string | null;
  emoji: string;
  reference_id: string | null;
  reference_type: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  display_name?: string;
  avatar_initials?: string;
}

// ─── Config ───────────────────────────────────────────────────────────────────
const ACTIVITY_CONFIG: Record<ActivityType, { label: string; icon: string; color: string; bg: string; border: string }> = {
  challenge_log: {
    label: 'Challenge Logged',
    icon: 'BoltIcon',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
  },
  goal_completed: {
    label: 'Goal Completed',
    icon: 'CheckCircleIcon',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
  },
  milestone_celebrated: {
    label: 'Milestone',
    icon: 'SparklesIcon',
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
  },
  streak_achievement: {
    label: 'Streak',
    icon: 'FireIcon',
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
  },
};

const FILTERS: { value: FilterType; label: string; emoji: string }[] = [
  { value: 'all', label: 'All Activity', emoji: '🌐' },
  { value: 'challenge_log', label: 'Challenges', emoji: '⚡' },
  { value: 'goal_completed', label: 'Goals', emoji: '✅' },
  { value: 'milestone_celebrated', label: 'Milestones', emoji: '✨' },
  { value: 'streak_achievement', label: 'Streaks', emoji: '🔥' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getInitials(name: string): string {
  return (name ?? 'AN')
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function timeAgo(dateStr: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const AVATAR_COLORS = [
  'bg-teal-500', 'bg-emerald-500', 'bg-blue-500', 'bg-purple-500',
  'bg-rose-500', 'bg-amber-500', 'bg-indigo-500', 'bg-cyan-500',
];

function avatarColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// ─── Activity Card ────────────────────────────────────────────────────────────
function ActivityCard({ item, isCurrentUser }: { item: ActivityItem; isCurrentUser: boolean }) {
  const cfg = ACTIVITY_CONFIG[item.activity_type];
  const initials = item.avatar_initials ?? getInitials(item.display_name ?? 'User');
  const name = item.display_name ?? 'Community Member';

  return (
    <div className={`bg-card border rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow duration-200 ${isCurrentUser ? 'border-primary/30 bg-primary/5' : 'border-border'}`}>
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 ${avatarColor(item.user_id)}`}>
          {initials}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-600 text-sm text-foreground">
              {isCurrentUser ? 'You' : name}
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-500 border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
              <Icon name={cfg.icon as Parameters<typeof Icon>[0]['name']} size={11} />
              {cfg.label}
            </span>
            <span className="text-xs text-muted-foreground ml-auto">{timeAgo(item.created_at)}</span>
          </div>

          <div className="mt-1.5 flex items-start gap-2">
            <span className="text-xl leading-none flex-shrink-0">{item.emoji}</span>
            <div>
              <p className="text-sm font-600 text-foreground leading-snug">{item.title}</p>
              {item.description && (
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{item.description}</p>
              )}
            </div>
          </div>

          {/* Metadata badges */}
          {item.metadata && Object.keys(item.metadata).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {item.metadata.streak_days && (
                <span className="text-xs bg-rose-50 text-rose-600 border border-rose-200 px-2 py-0.5 rounded-full font-500">
                  🔥 {String(item.metadata.streak_days)} day streak
                </span>
              )}
              {item.metadata.challenge_title && (
                <span className="text-xs bg-amber-50 text-amber-600 border border-amber-200 px-2 py-0.5 rounded-full font-500">
                  ⚡ {String(item.metadata.challenge_title)}
                </span>
              )}
              {item.metadata.goal_title && (
                <span className="text-xs bg-emerald-50 text-emerald-600 border border-emerald-200 px-2 py-0.5 rounded-full font-500">
                  🎯 {String(item.metadata.goal_title)}
                </span>
              )}
              {item.metadata.milestone && (
                <span className="text-xs bg-purple-50 text-purple-600 border border-purple-200 px-2 py-0.5 rounded-full font-500">
                  ✨ {String(item.metadata.milestone)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Live Indicator ───────────────────────────────────────────────────────────
function LiveIndicator({ newCount }: { newCount: number }) {
  if (newCount === 0) return null;
  return (
    <div className="flex items-center justify-center">
      <div className="inline-flex items-center gap-2 bg-primary text-primary-foreground text-xs font-600 px-4 py-2 rounded-full shadow-lg animate-bounce cursor-pointer">
        <span className="w-2 h-2 bg-white rounded-full animate-ping" />
        {newCount} new update{newCount > 1 ? 's' : ''}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ActivityStreamPage() {
  const { user } = useAuth();
  const supabase = createClient();

  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [newCount, setNewCount] = useState(0);
  const [pendingItems, setPendingItems] = useState<ActivityItem[]>([]);
  const latestIdRef = useRef<string | null>(null);

  // ── Fetch activity with user profile join ──────────────────────────────────
  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('community_activity')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (filter !== 'all') {
        query = query.eq('activity_type', filter);
      }

      const { data, error } = await query;
      if (error) throw error;

      const items = data ?? [];

      // Enrich with profile names
      const userIds = [...new Set(items.map((i) => i.user_id))];
      let profileMap: Record<string, { full_name: string }> = {};

      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('user_id, full_name')
          .in('user_id', userIds);

        (profiles ?? []).forEach((p: { user_id: string; full_name: string }) => {
          profileMap[p.user_id] = p;
        });
      }

      const enriched: ActivityItem[] = items.map((item) => ({
        ...item,
        display_name: profileMap[item.user_id]?.full_name ?? 'Community Member',
        avatar_initials: getInitials(profileMap[item.user_id]?.full_name ?? 'CM'),
      }));

      setActivities(enriched);
      if (enriched.length > 0) latestIdRef.current = enriched[0].id;
    } catch {
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [filter, supabase]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // ── Real-time subscription ─────────────────────────────────────────────────
  useEffect(() => {
    const channel = supabase
      .channel('community_activity_stream')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'community_activity' },
        async (payload) => {
          const newItem = payload.new as ActivityItem;

          // Enrich with profile
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('full_name')
            .eq('user_id', newItem.user_id)
            .single();

          const enriched: ActivityItem = {
            ...newItem,
            display_name: profile?.full_name ?? 'Community Member',
            avatar_initials: getInitials(profile?.full_name ?? 'CM'),
          };

          // If matches current filter, queue as pending
          if (filter === 'all' || filter === newItem.activity_type) {
            setPendingItems((prev) => [enriched, ...prev]);
            setNewCount((c) => c + 1);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabase, filter]);

  // ── Flush pending items ────────────────────────────────────────────────────
  const flushPending = useCallback(() => {
    if (pendingItems.length === 0) return;
    setActivities((prev) => [...pendingItems, ...prev]);
    setPendingItems([]);
    setNewCount(0);
  }, [pendingItems]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = {
    challenges: activities.filter((a) => a.activity_type === 'challenge_log').length,
    goals: activities.filter((a) => a.activity_type === 'goal_completed').length,
    milestones: activities.filter((a) => a.activity_type === 'milestone_celebrated').length,
    streaks: activities.filter((a) => a.activity_type === 'streak_achievement').length,
  };

  return (
    <AppLayout activeRoute="/activity-stream">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-700 text-foreground flex items-center gap-2">
              <span>🌐</span> Community Activity
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Real-time feed of challenges, goals, milestones &amp; streaks
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full font-500">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
              Live
            </span>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Challenges', value: stats.challenges, emoji: '⚡', color: 'text-amber-600' },
            { label: 'Goals', value: stats.goals, emoji: '✅', color: 'text-emerald-600' },
            { label: 'Milestones', value: stats.milestones, emoji: '✨', color: 'text-purple-600' },
            { label: 'Streaks', value: stats.streaks, emoji: '🔥', color: 'text-rose-600' },
          ].map((s) => (
            <div key={s.label} className="bg-card border border-border rounded-xl p-3 text-center shadow-sm">
              <div className="text-lg">{s.emoji}</div>
              <div className={`text-lg font-700 ${s.color}`}>{s.value}</div>
              <div className="text-xs text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => { setFilter(f.value); setNewCount(0); setPendingItems([]); }}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-500 border transition-all duration-150 ${
                filter === f.value
                  ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                  : 'bg-card text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
              }`}
            >
              <span>{f.emoji}</span>
              {f.label}
            </button>
          ))}
        </div>

        {/* New items notification */}
        {newCount > 0 && (
          <button onClick={flushPending} className="w-full">
            <LiveIndicator newCount={newCount} />
          </button>
        )}

        {/* Feed */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-card border border-border rounded-2xl p-4 animate-pulse">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-muted flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-muted rounded w-1/3" />
                    <div className="h-4 bg-muted rounded w-2/3" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : activities.length === 0 ? (
          <div className="bg-card border border-border rounded-2xl p-10 text-center">
            <div className="text-5xl mb-4">🌱</div>
            <h3 className="text-lg font-600 text-foreground mb-2">No activity yet</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Be the first to log a challenge, complete a goal, or hit a streak milestone!
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link
                href="/challenges"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-xl text-sm font-600 hover:bg-primary/90 transition-colors"
              >
                <Icon name="BoltIcon" size={16} />
                Browse Challenges
              </Link>
              <Link
                href="/shared-goals"
                className="inline-flex items-center gap-2 bg-card text-foreground border border-border px-4 py-2 rounded-xl text-sm font-600 hover:bg-muted transition-colors"
              >
                <Icon name="FlagIcon" size={16} />
                Shared Goals
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {activities.map((item) => (
              <ActivityCard
                key={item.id}
                item={item}
                isCurrentUser={item.user_id === user?.id}
              />
            ))}
          </div>
        )}

        {/* Quick Links */}
        {activities.length > 0 && (
          <div className="bg-card border border-border rounded-2xl p-4">
            <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-3">Join the action</p>
            <div className="grid grid-cols-2 gap-2">
              <Link href="/challenges" className="flex items-center gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors">
                <span className="text-xl">⚡</span>
                <div>
                  <p className="text-xs font-600 text-amber-700">Challenges</p>
                  <p className="text-xs text-amber-600">Compete &amp; grow</p>
                </div>
              </Link>
              <Link href="/shared-goals" className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors">
                <span className="text-xl">🎯</span>
                <div>
                  <p className="text-xs font-600 text-emerald-700">Shared Goals</p>
                  <p className="text-xs text-emerald-600">Grow together</p>
                </div>
              </Link>
              <Link href="/community-rankings" className="flex items-center gap-2 p-3 rounded-xl bg-purple-50 border border-purple-200 hover:bg-purple-100 transition-colors">
                <span className="text-xl">🏆</span>
                <div>
                  <p className="text-xs font-600 text-purple-700">Rankings</p>
                  <p className="text-xs text-purple-600">See leaderboards</p>
                </div>
              </Link>
              <Link href="/prayer-leaderboard" className="flex items-center gap-2 p-3 rounded-xl bg-rose-50 border border-rose-200 hover:bg-rose-100 transition-colors">
                <span className="text-xl">🔥</span>
                <div>
                  <p className="text-xs font-600 text-rose-700">Prayer Streaks</p>
                  <p className="text-xs text-rose-600">Keep it going</p>
                </div>
              </Link>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
